
import { useState } from 'react';

interface NavigationProps {
  scrolled: boolean;
  onBooking: () => void;
}

/**
 * Navigation component – displays a responsive header with smooth‑scroll
 * behaviour and optional mobile menu handling.
 *
 * The component is now defensive:
 *  • All DOM accesses are guarded to avoid errors during server‑side rendering.
 *  • `window.open` is wrapped in a try/catch block.
 *  • `scrollIntoView` is called only when the target element exists.
 */
export default function Navigation({
  scrolled,
  onBooking,
}: NavigationProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  /** Scrolls to a section identified by its HTML id. */
  const scrollToSection = (sectionId: string) => {
    // Guard against SSR where `document` is not defined.
    if (typeof document === 'undefined') return;

    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    } else {
      console.warn(`Navigation: No element found with id="${sectionId}"`);
    }
  };

  /** Opens WhatsApp link in a new tab – errors are caught so the UI never crashes. */
  const openWhatsApp = () => {
    try {
      if (typeof window !== 'undefined') {
        window.open('https://wa.me/message/FEG4Q7VZR4W2E1', '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.error('Failed to open WhatsApp link:', err);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo / Home link */}
          <div
            className="flex items-center space-x-2 cursor-pointer"
            onClick={() => scrollToSection('hero')}
          >
            <div className="w-10 h-10 flex items-center justify-center">
              <i
                className={`ri-home-heart-line text-2xl ${
                  scrolled ? 'text-teal-600' : 'text-white'
                }`}
              ></i>
            </div>
            <span
              className={`text-2xl font-serif font-bold ${
                scrolled ? 'text-gray-900' : 'text-white'
              }`}
            >
              Exquisite Spaces
            </span>
          </div>

          {/* Desktop navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection('portfolio')}
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                scrolled
                  ? 'text-gray-700 hover:text-teal-600'
                  : 'text-white hover:text-teal-300'
              }`}
            >
              Portfolio
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                scrolled
                  ? 'text-gray-700 hover:text-teal-600'
                  : 'text-white hover:text-teal-300'
              }`}
            >
              About
            </button>
            <button
              onClick={() => scrollToSection('services')}
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                scrolled
                  ? 'text-gray-700 hover:text-teal-600'
                  : 'text-white hover:text-teal-300'
              }`}
            >
              Services
            </button>
            <button
              onClick={() => scrollToSection('gallery')}
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                scrolled
                  ? 'text-gray-700 hover:text-teal-600'
                  : 'text-white hover:text-teal-300'
              }`}
            >
              Gallery
            </button>

            {/* WhatsApp */}
            <button
              onClick={openWhatsApp}
              className={`flex items-center gap-2 text-sm font-medium transition-colors whitespace-nowrap ${
                scrolled
                  ? 'text-gray-700 hover:text-green-600'
                  : 'text-white hover:text-green-300'
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-whatsapp-line text-base"></i>
              </span>
              WhatsApp
            </button>

            {/* Book a Consultation */}
            <button
              onClick={onBooking}
              className="px-5 py-2 border border-teal-600 text-teal-600 rounded-full text-sm font-medium hover:bg-teal-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              Book a Consultation
            </button>

            {/* Start Your Project */}
            <button
              onClick={() => scrollToSection('contact')}
              className="px-6 py-2 bg-teal-600 text-white rounded-full text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Start Your Project
            </button>
          </div>

          {/* Mobile menu toggle */}
          <button
            className="md:hidden"
            onClick={() => setMobileMenuOpen((prev) => !prev)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            <i
              className={`ri-menu-line text-2xl ${
                scrolled ? 'text-gray-900' : 'text-white'
              }`}
            ></i>
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 space-y-3">
            <button
              onClick={() => scrollToSection('portfolio')}
              className="block w-full text-left text-sm font-medium text-gray-700 hover:text-teal-600 whitespace-nowrap"
            >
              Portfolio
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className="block w-full text-left text-sm font-medium text-gray-700 hover:text-teal-600 whitespace-nowrap"
            >
              About
            </button>
            <button
              onClick={() => scrollToSection('services')}
              className="block w-full text-left text-sm font-medium text-gray-700 hover:text-teal-600 whitespace-nowrap"
            >
              Services
            </button>
            <button
              onClick={() => scrollToSection('gallery')}
              className="block w-full text-left text-sm font-medium text-gray-700 hover:text-teal-600 whitespace-nowrap"
            >
              Gallery
            </button>
            <button
              onClick={() => {
                openWhatsApp();
                setMobileMenuOpen(false);
              }}
              className="flex items-center gap-2 w-full text-left text-sm font-medium text-gray-700 hover:text-green-600 whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-whatsapp-line text-base"></i>
              </span>
              WhatsApp
            </button>
            <button
              onClick={() => {
                onBooking();
                setMobileMenuOpen(false);
              }}
              className="block w-full text-left px-6 py-2 border border-teal-600 text-teal-600 rounded-full text-sm font-medium hover:bg-teal-50 whitespace-nowrap cursor-pointer"
            >
              Book a Consultation
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="block w-full text-left px-6 py-2 bg-teal-600 text-white rounded-full text-sm font-medium hover:bg-teal-700 whitespace-nowrap cursor-pointer"
            >
              Start Your Project
            </button>
          </div>
        )}
      </div>
    </nav>
  );
}
